import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LabelIcon from '@mui/icons-material/Label';
import ScaleIcon from '@mui/icons-material/MonitorWeight';
import { useTranslation } from 'react-i18next';

interface FeaturesMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  bulkActionMode: boolean;
  loading: boolean;
  loadingExport: boolean;
  n11IntegrationActive: boolean;
  isMobile: boolean;
  onOpenCategoryModal: () => void;
  onOpenProductFeatureModal: () => void;
  onOpenBrandModal: () => void;
  onOpenWarehouseModal: () => void;
  onStockMovementsReport: () => void;
  onToggleBulkActionMode: () => void;
  onNavigateToImageLibrary: () => void;
  onOpenTableSettings: () => void;
  onOpenShipmentTemplates: () => void;
  onExportToExcel: () => void;
  onOpenImportModal: () => void;
  onOpenN11Upload: () => void;
  onOpenN11Log: () => void;
  onOpenOtherActionsModal: () => void;
  onPriceChangeLogs: () => void;
  onLabelPrinting: () => void;
  onScaleSync: () => void;
  onProductRecipesPage: () => void;
  onProductMenusPage: () => void;
  /** Stok Sayım sayfası; verilmezse menüde görünmez. */
  onStockCountPage?: () => void;
}

/**
 * Menü satırı tanımı. Öğeler JSX yerine veri olarak tutuluyor çünkü kullanılabilir
 * olanları başa, pasifleri sona almak sıralama işi; JSX ile yazıldığında bu sıra
 * elle korunmak zorunda kalıyor ve N11 gibi koşullu pasif öğeler yanlış yere düşüyor.
 */
interface FeatureMenuEntry {
  key: string;
  label: string;
  secondary?: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  hidden?: boolean;
}

export const FeaturesMenu: React.FC<FeaturesMenuProps> = ({
  anchorEl,
  open,
  onClose,
  bulkActionMode,
  loading,
  loadingExport,
  n11IntegrationActive,
  isMobile,
  onOpenCategoryModal,
  onOpenProductFeatureModal,
  onOpenBrandModal,
  onOpenWarehouseModal,
  onStockMovementsReport,
  onToggleBulkActionMode,
  onNavigateToImageLibrary,
  onOpenTableSettings,
  onOpenShipmentTemplates,
  onExportToExcel,
  onOpenImportModal,
  onOpenN11Upload,
  onOpenN11Log,
  onOpenOtherActionsModal,
  onPriceChangeLogs,
  onLabelPrinting,
  onScaleSync,
  onProductRecipesPage,
  onProductMenusPage,
  onStockCountPage,
}) => {
  const { t } = useTranslation('accounting');

  const entries: FeatureMenuEntry[] = [
    {
      key: 'reports',
      label: t('stock.menu.reports'),
      icon: <AssessmentIcon fontSize="small" />,
      onClick: onStockMovementsReport,
    },
    ...(onStockCountPage ? [{
      key: 'stockCount',
      label: t('stock.menu.stockCount', 'Stok Sayım'),
      icon: <FactCheckIcon fontSize="small" />,
      onClick: onStockCountPage,
    }] : []),
    {
      key: 'categories',
      label: t('stock.menu.categories'),
      icon: <CategoryIcon fontSize="small" />,
      onClick: onOpenCategoryModal,
    },
    {
      key: 'productFeatures',
      label: t('stock.menu.productFeatures'),
      icon: <TuneIcon fontSize="small" />,
      onClick: onOpenProductFeatureModal,
    },
    {
      key: 'brands',
      label: t('stock.menu.brands'),
      icon: <BrandingWatermarkIcon fontSize="small" />,
      onClick: onOpenBrandModal,
    },
    {
      key: 'warehouses',
      label: t('stock.menu.warehouses'),
      icon: <WarehouseIcon fontSize="small" />,
      onClick: onOpenWarehouseModal,
    },
    {
      key: 'productRecipes',
      label: t('stock.menu.productRecipes'),
      icon: <RestaurantMenuIcon fontSize="small" />,
      onClick: onProductRecipesPage,
    },
    {
      key: 'productMenus',
      label: t('stock.menu.productMenus'),
      icon: <MenuBookIcon fontSize="small" />,
      onClick: onProductMenusPage,
    },
    {
      key: 'bulkActions',
      label: t('stock.menu.bulkActions'),
      icon: <CallToActionIcon fontSize="small" color={bulkActionMode ? 'warning' : 'primary'} />,
      onClick: onToggleBulkActionMode,
    },
    {
      key: 'imageLibrary',
      label: t('stock.menu.imageLibrary'),
      icon: <PhotoLibraryIcon fontSize="small" />,
      onClick: onNavigateToImageLibrary,
    },
    {
      key: 'customizeTable',
      label: t('stock.menu.customizeTable'),
      icon: <ViewColumnIcon fontSize="small" />,
      onClick: onOpenTableSettings,
    },
    {
      key: 'shipmentTemplates',
      label: t('stock.menu.shipmentTemplates'),
      icon: <LocalShippingIcon fontSize="small" />,
      onClick: onOpenShipmentTemplates,
    },
    {
      key: 'downloadExcel',
      label: loadingExport ? t('stock.common.preparing') : t('stock.menu.downloadExcel'),
      icon: <FileDownloadIcon fontSize="small" />,
      onClick: onExportToExcel,
      disabled: loadingExport || loading,
    },
    {
      key: 'importStock',
      label: t('stock.menu.importStock'),
      icon: <FileUploadIcon fontSize="small" />,
      onClick: onOpenImportModal,
    },
    {
      key: 'marketplaceLogs',
      label: t('stock.menu.marketplaceLogs'),
      icon: <AssessmentIcon fontSize="small" />,
      onClick: onOpenN11Log,
    },
    {
      key: 'priceChangeLogs',
      label: t('stock.menu.priceChangeLogs'),
      icon: <HistoryIcon fontSize="small" />,
      onClick: onPriceChangeLogs,
    },
    {
      key: 'labelPrinting',
      label: t('stock.menu.labelPrinting'),
      icon: <LabelIcon fontSize="small" />,
      onClick: onLabelPrinting,
    },
    {
      key: 'scaleSync',
      label: t('stock.menu.scaleSync'),
      icon: <ScaleIcon fontSize="small" />,
      onClick: onScaleSync,
    },
    {
      key: 'otherActions',
      label: t('stock.menu.otherActions'),
      icon: <MoreHorizIcon fontSize="small" />,
      onClick: onOpenOtherActionsModal,
      hidden: !isMobile,
    },
    {
      key: 'n11Catalog',
      label: `${t('stock.menu.n11Catalog')} ${n11IntegrationActive ? '✅' : '❌'}`,
      secondary: !n11IntegrationActive ? t('stock.menu.enterIntegrationInfo') : undefined,
      icon: <CloudUploadIcon fontSize="small" />,
      onClick: onOpenN11Upload,
      disabled: !n11IntegrationActive,
    },
    {
      key: 'hepsiburadaCatalog',
      label: t('stock.menu.hepsiburadaCatalog'),
      secondary: t('stock.menu.comingSoon'),
      icon: <CloudUploadIcon fontSize="small" />,
      disabled: true,
    },
    {
      key: 'trendyolCatalog',
      label: t('stock.menu.trendyolCatalog'),
      secondary: t('stock.menu.comingSoon'),
      icon: <CloudUploadIcon fontSize="small" />,
      disabled: true,
    },
  ];

  const visibleEntries = entries.filter((entry) => !entry.hidden);
  // Kullanılabilir öğeler önce, pasifler en sonda — her iki grup içinde
  // yukarıdaki yazım sırası korunur.
  const orderedEntries = [
    ...visibleEntries.filter((entry) => !entry.disabled),
    ...visibleEntries.filter((entry) => entry.disabled),
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          maxHeight: '75vh',
          // Dar ekranda menü ekranın dışına taşmasın; iki sütun yalnızca yer
          // varken açılır (aşağıdaki gridTemplateColumns ile birlikte).
          maxWidth: 'calc(100vw - 24px)',
        },
      }}
      MenuListProps={{
        sx: {
          display: 'grid',
          // xs: tek sütun (ekran genişliği yetmiyor, dikeyde kaydırılır)
          // sm+: iki sütun — liste yüksekliği yarıya iner, taşma biter
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(240px, 1fr))' },
          columnGap: 0.5,
          py: 0.5,
        },
      }}
    >
      {orderedEntries.map((entry) => (
        <MenuItem
          key={entry.key}
          disabled={entry.disabled}
          onClick={
            entry.onClick
              ? () => {
                  onClose();
                  entry.onClick!();
                }
              : undefined
          }
          sx={
            entry.disabled
              ? {
                  opacity: 0.6,
                  '&.Mui-disabled': { color: 'text.secondary' },
                }
              : undefined
          }
        >
          <ListItemIcon>{entry.icon}</ListItemIcon>
          <ListItemText primary={entry.label} secondary={entry.secondary} />
        </MenuItem>
      ))}
    </Menu>
  );
};
